import {
  Alert,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import DeleteTwoToneIcon from "@mui/icons-material/DeleteTwoTone";
import React, { useEffect, useState } from "react";
import CompanyService from "../../Services/Company.service";
import {
  IOnboardingInvoicing,
  IOnboardingWizardState,
  IOnboardingXeroKey,
} from "../../Interfaces/IOnboardingWizardState";
import RichTextEditor from "src/Shared/Components/RichTextEditor/RichTextEditor";
import {
  incompleteXeroKeyIndexes,
  isValidEmail,
  seedXeroKeys,
  showsHealthTickPurchaseOrder,
  XERO_KEY_NAME_MAX_LENGTH,
  xeroKeyNameProblem,
} from "./OnboardingCapture.utils";
import { useWriteThrough, WriteThroughResult } from "./useWriteThrough";

type Props = {
  state: IOnboardingWizardState;
  companyRowID: string;
  frozen: boolean;
  registerFlush: (flush: () => Promise<WriteThroughResult>) => void;
  /** Set once an advance has been refused, so a blank required field is marked too (bug 5832). */
  showFieldErrors?: boolean;
};

const OTHER_ARRANGEMENT = "Other";

/**
 * Wizard step 5 — Invoicing (stories 5608–5611).
 *
 * The arrangement cards come from the server already filtered for the client's country, so the SSO
 * options only ever appear for an Australian client and a stored SSO choice is cleared when the
 * country moves off Australia (S5.1). Everything on this step is the onboarding's own payload,
 * written through as it is typed.
 */
const Step5Invoicing: React.FC<Props> = ({
  state,
  companyRowID,
  frozen,
  registerFlush,
  showFieldErrors,
}) => {
  const [invoicing, setInvoicing] = useState<IOnboardingInvoicing>({
    arrangement: state.invoicing?.arrangement ?? null,
    otherArrangementDescription: state.invoicing?.otherArrangementDescription ?? null,
    invoiceEmail: state.invoicing?.invoiceEmail ?? null,
    xeroKeys: seedXeroKeys(state.invoicing?.xeroKeys),
    billingContactName: state.invoicing?.billingContactName ?? null,
    billingPhone: state.invoicing?.billingPhone ?? null,
    invoiceDetailOptions: state.invoicing?.invoiceDetailOptions || [],
    paymentTerms: state.invoicing?.paymentTerms ?? "Standard20th",
    otherPaymentTerms: state.invoicing?.otherPaymentTerms ?? null,
    deliveryMethod: state.invoicing?.deliveryMethod ?? "Email",
    purchaseOrder:
      state.invoicing?.purchaseOrder ?? {
        required: false,
        number: null,
        healthTickNumber: null,
        invoicesPerPurchaseOrder: null,
        formatRules: null,
        contactDetails: null,
      },
  });
  const [financeDetails, setFinanceDetails] = useState<string>(
    state.information?.financeDetails || "",
  );

  const writer = useWriteThrough<{ invoicing: IOnboardingInvoicing; financeDetails: string }>(
    (patch) =>
      CompanyService.saveOnboardingDraft({
        companyRowID,
        invoicing: patch.invoicing ?? invoicing,
        information: {
          financeDetails: patch.financeDetails ?? financeDetails,
          bookingDetails: state.information?.bookingDetails ?? null,
          occfNotes: state.information?.occfNotes ?? null,
        },
      }),
  );

  useEffect(() => {
    registerFlush(() => writer.flush());
  }, [registerFlush, writer]);

  const update = (changes: Partial<IOnboardingInvoicing>) => {
    const next = { ...invoicing, ...changes };
    setInvoicing(next);
    if (!frozen) writer.schedule({ invoicing: next, financeDetails });
  };

  const updateKey = (index: number, changes: Partial<IOnboardingXeroKey>) => {
    const xeroKeys = invoicing.xeroKeys.map((key, keyIndex) =>
      keyIndex === index ? { ...key, ...changes } : key,
    );
    update({ xeroKeys });
  };

  const addKey = () =>
    update({
      xeroKeys: [
        ...invoicing.xeroKeys,
        {
          name: "",
          keyValue: null,
          invoiceTiming: null,
          allServices: true,
          allOffices: true,
          serviceCategories: [],
          siteIDs: [],
        },
      ],
    });

  const removeKey = (index: number) =>
    update({ xeroKeys: invoicing.xeroKeys.filter((_, keyIndex) => keyIndex !== index) });

  const applyExistingContact = (contactID: string) => {
    const contact = state.contacts.find((x) => `${x.companyContactID}` === contactID);
    if (!contact) return;
    // Values are copied, not linked: a later contact edit does not follow through (S5.4).
    update({
      billingContactName: contact.fullName || null,
      invoiceEmail: contact.emailAddress || null,
      billingPhone: contact.phoneNumber || null,
    });
  };

  const isOther = invoicing.arrangement === OTHER_ARRANGEMENT;
  const requireAllocation = invoicing.xeroKeys.length > 1;
  const incompleteKeys = incompleteXeroKeyIndexes(invoicing.xeroKeys, requireAllocation);
  const emailMalformed = Boolean(invoicing.invoiceEmail) && !isValidEmail(invoicing.invoiceEmail);
  // A malformed address is marked as it is typed; an empty one — also invalid, since the field is
  // required — is only marked once an advance has been refused, so a pristine step 5 is not shown
  // as already broken (bug 5668). Both used to reduce to the same isValidEmail(empty) === false
  // check server-side, but the field's own error state only ever looked at the malformed half,
  // so a blank address blocked the step with the banner and no field marked at all (bug 5832).
  const emailMissing = Boolean(showFieldErrors) && !invoicing.invoiceEmail?.trim();
  const emailInvalid = emailMalformed || emailMissing;

  return (
    <>
      <FormControl sx={{ mb: 2 }}>
        <FormLabel sx={{ fontSize: 13 }}>How is the client invoiced?</FormLabel>
        <RadioGroup
          value={invoicing.arrangement || ""}
          onChange={(e) => update({ arrangement: e.target.value })}
        >
          {state.references.invoicingArrangements.map((option) => (
            <FormControlLabel
              key={option.key}
              value={option.key}
              control={<Radio size="small" disabled={frozen} />}
              label={option.label}
            />
          ))}
        </RadioGroup>
      </FormControl>

      {isOther && (
        <TextField
          fullWidth
          size="small"
          required
          label="Describe the arrangement"
          disabled={frozen}
          value={invoicing.otherArrangementDescription || ""}
          onChange={(e) => update({ otherArrangementDescription: e.target.value })}
          sx={{ mb: 2 }}
        />
      )}

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
        Billing
      </Typography>

      {!frozen && state.contacts.length > 0 && (
        <TextField
          select
          size="small"
          label="Use an existing contact"
          value=""
          sx={{ mb: 2, minWidth: 260 }}
          onChange={(e) => applyExistingContact(e.target.value)}
        >
          {state.contacts.map((contact) => (
            <MenuItem key={contact.companyContactID} value={`${contact.companyContactID}`}>
              {contact.fullName}
            </MenuItem>
          ))}
        </TextField>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            size="small"
            required
            label="Invoice Emailing Address"
            disabled={frozen}
            error={emailInvalid}
            helperText={
              emailMissing
                ? "Enter the invoice emailing address."
                : emailMalformed
                  ? "Enter a valid email address."
                  : " "
            }
            value={invoicing.invoiceEmail || ""}
            onChange={(e) => update({ invoiceEmail: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            size="small"
            label="Billing Contact Name"
            disabled={frozen}
            value={invoicing.billingContactName || ""}
            onChange={(e) => update({ billingContactName: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            size="small"
            label="Billing Phone"
            disabled={frozen}
            value={invoicing.billingPhone || ""}
            onChange={(e) => update({ billingPhone: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            select
            fullWidth
            size="small"
            label="Invoice Delivery"
            disabled={frozen}
            value={invoicing.deliveryMethod || "Email"}
            onChange={(e) => update({ deliveryMethod: e.target.value })}
          >
            {state.references.invoiceDeliveryMethods.map((method) => (
              <MenuItem key={method} value={method}>
                {method}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            select
            fullWidth
            size="small"
            label="Payment Terms"
            disabled={frozen}
            value={invoicing.paymentTerms || "Standard20th"}
            onChange={(e) => update({ paymentTerms: e.target.value })}
          >
            {state.references.paymentTerms.map((option) => (
              <MenuItem key={option.key} value={option.key}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        {/* "Other" is no longer a payment term (story 6360): the list is Imperans's eight rows and
            nothing else. Text typed while it was offered is kept on the payload and routed to the
            Imperans review ticket, so it is shown read-only rather than lost. */}
        {invoicing.otherPaymentTerms && (
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="Payment terms detail (no longer offered)"
              disabled
              value={invoicing.otherPaymentTerms}
              helperText="Choose one of the listed terms; this note travels to the Imperans setup team."
            />
          </Grid>
        )}
      </Grid>

      <Divider sx={{ my: 2 }} />

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="subtitle2" fontWeight={600}>
          Xero keys ({invoicing.xeroKeys.length})
        </Typography>
        {!frozen && (
          <Button size="small" onClick={addKey}>
            Add key
          </Button>
        )}
      </Box>

      {invoicing.xeroKeys.map((key, index) => (
        <Paper
          key={index}
          elevation={0}
          sx={{
            p: 2,
            mt: 1,
            border: "1px solid",
            borderColor: incompleteKeys.includes(index) ? "warning.main" : "divider",
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                required
                label="Name"
                disabled={frozen}
                error={!key.name?.trim() || Boolean(xeroKeyNameProblem(invoicing.xeroKeys, index))}
                helperText={
                  !key.name?.trim()
                    ? "Enter a name for this key."
                    : xeroKeyNameProblem(invoicing.xeroKeys, index) ||
                      `${key.name.trim().length}/${XERO_KEY_NAME_MAX_LENGTH}`
                }
                // Imperans's cap (bug 6495): a longer paste is cut at the cap rather than refused later.
                inputProps={{ maxLength: XERO_KEY_NAME_MAX_LENGTH }}
                value={key.name || ""}
                onChange={(e) => updateKey(index, { name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                required
                label="Xero Key"
                disabled={frozen}
                error={!key.keyValue?.trim()}
                helperText={!key.keyValue?.trim() ? "Enter the Xero key." : " "}
                value={key.keyValue || ""}
                onChange={(e) => updateKey(index, { keyValue: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                size="small"
                required
                label="Invoice timing"
                disabled={frozen}
                error={!key.invoiceTiming}
                helperText={!key.invoiceTiming ? "Choose the invoice timing." : " "}
                value={key.invoiceTiming || ""}
                onChange={(e) => updateKey(index, { invoiceTiming: e.target.value })}
              >
                <MenuItem value="">Select…</MenuItem>
                {state.references.invoiceTimings.map((timing) => (
                  <MenuItem key={timing.key} value={timing.key}>
                    {timing.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={1}>
              {/* The remove control is hidden while only one key exists — the list can never
                  empty (S5.2). */}
              {!frozen && invoicing.xeroKeys.length > 1 && (
                <IconButton size="small" onClick={() => removeKey(index)}>
                  <DeleteTwoToneIcon fontSize="small" />
                </IconButton>
              )}
            </Grid>
          </Grid>

          {/* The allocation matrix is hidden with a single key and appears from the second (S5.6). */}
          {requireAllocation && (
            <Box sx={{ mt: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    disabled={frozen}
                    checked={key.allServices}
                    onChange={(e) => updateKey(index, { allServices: e.target.checked })}
                  />
                }
                label="All services"
              />
              {!key.allServices && (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, pl: 3 }}>
                  {state.references.serviceCategories.map((category) => (
                    <FormControlLabel
                      key={category.key}
                      control={
                        <Checkbox
                          size="small"
                          disabled={frozen}
                          checked={(key.serviceCategories || []).includes(category.key)}
                          onChange={(e) =>
                            updateKey(index, {
                              serviceCategories: e.target.checked
                                ? [...(key.serviceCategories || []), category.key]
                                : (key.serviceCategories || []).filter((x) => x !== category.key),
                            })
                          }
                        />
                      }
                      label={category.label}
                    />
                  ))}
                </Box>
              )}
              {/* The orange card border alone said nothing: a site removed on step 3 can cascade to
                  strip a key's only office, leaving allOffices false and siteIDs empty with no cue
                  beyond the outline — no message, no error state, nothing naming the key or the
                  reason (task 5841). Named per allocation half, since either can be the one left
                  empty, and shown only once requireAllocation actually applies (S5.6). */}
              {requireAllocation && !key.allServices && !(key.serviceCategories || []).length && (
                <Typography variant="caption" color="error" display="block" sx={{ pl: 3 }}>
                  Select at least one service category, or tick All services.
                </Typography>
              )}

              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    disabled={frozen}
                    checked={key.allOffices}
                    onChange={(e) => updateKey(index, { allOffices: e.target.checked })}
                  />
                }
                label="All offices"
              />
              {!key.allOffices && (
                <Box sx={{ pl: 3 }}>
                  {state.sites.length === 0 ? (
                    <Typography variant="caption" color="text.secondary">
                      No sites yet — add them on step 3 before scoping this key to offices.
                    </Typography>
                  ) : (
                    state.sites.map((site) => (
                      <FormControlLabel
                        key={site.companySiteID}
                        control={
                          <Checkbox
                            size="small"
                            disabled={frozen}
                            checked={(key.siteIDs || []).includes(site.companySiteID)}
                            onChange={(e) =>
                              updateKey(index, {
                                siteIDs: e.target.checked
                                  ? [...(key.siteIDs || []), site.companySiteID]
                                  : (key.siteIDs || []).filter((x) => x !== site.companySiteID),
                              })
                            }
                          />
                        }
                        label={site.officeName}
                      />
                    ))
                  )}
                </Box>
              )}
              {requireAllocation && !key.allOffices && !(key.siteIDs || []).length && state.sites.length > 0 && (
                <Typography variant="caption" color="error" display="block" sx={{ pl: 3 }}>
                  Select at least one office, or tick All offices — a site this key was scoped to
                  may have been removed.
                </Typography>
              )}
            </Box>
          )}
        </Paper>
      ))}

      {!requireAllocation && (
        <Typography variant="caption" color="text.secondary">
          A single key covers all services and all offices.
        </Typography>
      )}

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" fontWeight={600}>
        Details required on the invoice
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
        {state.references.invoiceDetailOptions.map((option) => (
          <FormControlLabel
            key={option.key}
            control={
              <Checkbox
                size="small"
                disabled={frozen}
                checked={invoicing.invoiceDetailOptions.includes(option.key)}
                onChange={(e) =>
                  update({
                    invoiceDetailOptions: e.target.checked
                      ? [...invoicing.invoiceDetailOptions, option.key]
                      : invoicing.invoiceDetailOptions.filter((x) => x !== option.key),
                  })
                }
              />
            }
            label={option.label}
          />
        ))}
      </Box>

      <Divider sx={{ my: 2 }} />

      <FormControl>
        <FormLabel sx={{ fontSize: 13 }}>Purchase orders</FormLabel>
        <RadioGroup
          row
          value={invoicing.purchaseOrder?.required ? "yes" : "no"}
          onChange={(e) =>
            update({
              purchaseOrder: {
                ...invoicing.purchaseOrder,
                required: e.target.value === "yes",
              },
            })
          }
        >
          <FormControlLabel value="no" control={<Radio size="small" disabled={frozen} />} label="No PO required" />
          <FormControlLabel value="yes" control={<Radio size="small" disabled={frozen} />} label="PO required" />
        </RadioGroup>
      </FormControl>

      {/* Switching back hides these and keeps what was typed (S5.5). */}
      {invoicing.purchaseOrder?.required && (
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="PO Number"
              disabled={frozen}
              value={invoicing.purchaseOrder.number || ""}
              onChange={(e) =>
                update({
                  purchaseOrder: { ...invoicing.purchaseOrder, number: e.target.value },
                })
              }
            />
          </Grid>
          {showsHealthTickPurchaseOrder(state) && (
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="HealthTick PO Number"
                disabled={frozen}
                value={invoicing.purchaseOrder.healthTickNumber || ""}
                onChange={(e) =>
                  update({
                    purchaseOrder: {
                      ...invoicing.purchaseOrder,
                      healthTickNumber: e.target.value,
                    },
                  })
                }
              />
            </Grid>
          )}
          <Grid item xs={12} md={4}>
            <TextField
              select
              fullWidth
              size="small"
              label="Invoices per PO"
              disabled={frozen}
              value={invoicing.purchaseOrder.invoicesPerPurchaseOrder || ""}
              onChange={(e) =>
                update({
                  purchaseOrder: {
                    ...invoicing.purchaseOrder,
                    invoicesPerPurchaseOrder: e.target.value,
                  },
                })
              }
            >
              <MenuItem value="One">One</MenuItem>
              <MenuItem value="Multiple">Multiple</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="PO Format / Rules"
              disabled={frozen}
              value={invoicing.purchaseOrder.formatRules || ""}
              onChange={(e) =>
                update({
                  purchaseOrder: { ...invoicing.purchaseOrder, formatRules: e.target.value },
                })
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="PO Contact Details"
              disabled={frozen}
              value={invoicing.purchaseOrder.contactDetails || ""}
              onChange={(e) =>
                update({
                  purchaseOrder: { ...invoicing.purchaseOrder, contactDetails: e.target.value },
                })
              }
            />
          </Grid>
        </Grid>
      )}

      <Divider sx={{ my: 2 }} />

      <RichTextEditor
        label="Finance Details"
        value={financeDetails}
        disabled={frozen}
        helperText="Carried to the client's finance record in Imperans — never customer-facing."
        onChange={(html) => {
          setFinanceDetails(html);
          if (!frozen) writer.schedule({ invoicing, financeDetails: html });
        }}
      />

      <Alert severity="info" sx={{ mt: 2 }}>
        Special pricing is captured once, on step 1 — it reads{" "}
        <strong>{state.specialPricing ? "Yes" : "No"}</strong> for this client. Put the detail in
        Finance Details.
      </Alert>
    </>
  );
};

export default Step5Invoicing;
